/**
 * RecommendationPlan — the Recommendation-Plan chip row (Smart
 * Recommendations R1, frontend spec §7 task 1).
 *
 * Renders one accessible chip per plan Need (`event: plan`, or rehydrated
 * from a reloaded thread's `plan_json` via `plan-rehydrate.ts`). Each chip is
 * a real `<button>` (keyboard-operable, visible focus ring) whose
 * `aria-pressed` reflects the Need's explored ("done") state:
 *   - idle    — not yet fetched in this thread
 *   - loading — its `fetch_need` tap is in flight (spinner; stays focusable —
 *               NOT `disabled`, so Tab order and SR reachability don't break)
 *   - done    — already fetched in this thread (check mark, `aria-pressed`)
 *   - error   — the last fetch failed (inline retry — tap the chip again)
 * The `priority === 1` Need additionally shows a sparkle + a "Recommended"
 * caption with its `why` — only when that `why` is non-empty (the backend
 * leaves it empty when the model wrote no reason; a "Recommended:" label with
 * nothing after it is noise). A second, small trailing button per Need ("See all
 * →") opens the full, filterable category (`onSeeAllNeed`) — the chat-linear
 * "tabs that expand" escape (spec §7 task 4) — independent of fetch state.
 *
 * A visually-hidden `aria-live="polite"` region announces state changes
 * (done / error) as they happen, so a screen-reader user learns the outcome
 * of a chip tap without having to re-focus it.
 *
 * Design: Smart Recommendations R1 postdates the design canvas — no
 * `docs/GardEn/src/screens/Chat.jsx` state covers this. Drafted consistent
 * with the system per DESIGN-SYSTEM.md's "missing state" rule: the SAME pill
 * anatomy + `--g-*` tokens as `FollowupPills`/`GPill` (an uppercase label row
 * above a wrapped chip row) — not a new visual language.
 */
import { Icon } from "@garden/ui";
import { Loader2 } from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";

import type { Need } from "@/features/chat/useChatStream";

export interface RecommendationPlanProps {
  needs: Need[];
  /** `need_id`s already fetched in this thread (done state). */
  explored: ReadonlySet<string>;
  /** `need_id`s whose `fetch_need` call is in flight right now. */
  loading: ReadonlySet<string>;
  /** `need_id`s whose last fetch failed (inline retry via re-tap). */
  errored: ReadonlySet<string>;
  /** Tap a chip — fetches (or retries) that Need. */
  onFetchNeed: (needId: string) => void;
  /** "See all →" — opens the full, filterable category for that Need. */
  onSeeAllNeed: (need: Need) => void;
}

function chipStateLabel(
  done: boolean,
  loading: boolean,
  errored: boolean,
): "idle" | "loading" | "done" | "error" {
  if (loading) return "loading";
  if (done) return "done";
  if (errored) return "error";
  return "idle";
}

export function RecommendationPlan({
  needs,
  explored,
  loading,
  errored,
  onFetchNeed,
  onSeeAllNeed,
}: RecommendationPlanProps): ReactElement | null {
  const { t } = useTranslation("chat");
  const reactId = useId();
  const whyId = `chat-plan-why-${reactId.replace(/:/g, "")}`;

  const prevExploredRef = useRef<ReadonlySet<string>>(explored);
  const prevErroredRef = useRef<ReadonlySet<string>>(errored);
  const [announcement, setAnnouncement] = useState("");
  /**
   * Change-persistence (RAISE, donor: gate board): the need that most recently
   * RESOLVED keeps the `--g-mark` wash so a user whose eyes were on the answer
   * can still see which chip produced it. It is replaced when the next need
   * resolves — it never times out, because "seen" is the user acting, not a
   * timer expiring.
   *
   * This raise was written into the direction contract and then implemented on
   * settings, landing and shops — every surface EXCEPT the one the contract
   * names ("a resolved need or a changed price"), both of which live only here.
   */
  const [justResolvedId, setJustResolvedId] = useState<string | null>(null);

  // SR live-region announcement on a state change (a11y requirement): fires
  // once per NEWLY-explored or NEWLY-errored Need, not on every render.
  useEffect(() => {
    const prevExplored = prevExploredRef.current;
    const newlyDone = needs.find(
      (n) => explored.has(n.need_id) && !prevExplored.has(n.need_id),
    );
    if (newlyDone) {
      setAnnouncement(t("plan.announceDone", { label: newlyDone.label }));
      setJustResolvedId(newlyDone.need_id);
    } else {
      const prevErrored = prevErroredRef.current;
      const newlyErrored = needs.find(
        (n) => errored.has(n.need_id) && !prevErrored.has(n.need_id),
      );
      if (newlyErrored) {
        setAnnouncement(t("plan.announceError", { label: newlyErrored.label }));
      }
    }
    prevExploredRef.current = explored;
    prevErroredRef.current = errored;
    // Only the sets + the (stable-per-plan) needs list drive a re-derivation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explored, errored, needs]);

  if (needs.length === 0) return null;

  const sorted = [...needs].sort((a, b) => a.priority - b.priority);
  const top = sorted.find((n) => n.priority === 1) ?? null;
  // The caption needs a reason to show. Prompt v2 asks the model for ONE
  // plan-level reason (`top_why`), which the backend places on this need and
  // leaves empty when the model wrote none — so the caption, and the chip's
  // `aria-describedby` pointing at it, exist only when there is text.
  const topWhy = top?.why.trim() ?? "";
  const showWhy = top !== null && topWhy.length > 0;

  return (
    <div className="mt-2.5" data-testid="chat-plan">
      <div className="mb-1.5 px-1 text-[12px] font-bold uppercase tracking-[0.03em] text-muted-foreground">
        {t("plan.title")}
      </div>
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label={t("plan.title")}
      >
        {sorted.map((need) => {
          const done = explored.has(need.need_id);
          const isLoading = loading.has(need.need_id);
          const isErrored = errored.has(need.need_id);
          const state = chipStateLabel(done, isLoading, isErrored);
          return (
            <div
              key={need.need_id}
              className={
                "inline-flex items-stretch overflow-hidden rounded-full " +
                (justResolvedId === need.need_id ? "g-mark" : "")
              }
              data-testid={`chat-plan-item-${need.need_id}`}
              data-state={state}
              data-marked={justResolvedId === need.need_id ? "true" : undefined}
            >
              <button
                type="button"
                data-testid={`chat-plan-chip-${need.need_id}`}
                aria-pressed={done}
                aria-busy={isLoading}
                aria-describedby={
                  need.priority === 1 && showWhy ? whyId : undefined
                }
                onClick={() => onFetchNeed(need.need_id)}
                className={
                  "inline-flex h-8 items-center gap-1.5 rounded-l-full px-3 text-[15px]/[1.429] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
                  (done
                    ? "bg-primary text-primary-foreground"
                    : isErrored
                      ? "bg-[var(--g-error)]/10 text-[var(--g-error)]"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80")
                }
              >
                {need.priority === 1 ? (
                  <Icon name="sparkles" size={13} aria-hidden="true" />
                ) : null}
                {isLoading ? (
                  <Loader2
                    className="size-3.5 animate-spin"
                    aria-hidden="true"
                  />
                ) : done ? (
                  <Icon name="check" size={13} aria-hidden="true" />
                ) : null}
                <span className="max-w-[160px] truncate">{need.label}</span>
              </button>
              <button
                type="button"
                data-testid={`chat-plan-seeall-${need.need_id}`}
                aria-label={t("plan.seeAllAria", { label: need.label })}
                onClick={() => onSeeAllNeed(need)}
                className="inline-flex h-8 items-center whitespace-nowrap rounded-r-full border-l border-[color:var(--g-hairline)] bg-secondary px-2 text-[13px]/[1.333] font-medium text-secondary-foreground transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {t("plan.seeAll")}
              </button>
            </div>
          );
        })}
      </div>
      {showWhy ? (
        <p
          id={whyId}
          className="mt-1.5 px-1 text-[13px] leading-relaxed text-muted-foreground"
          data-testid="chat-plan-why"
        >
          <span className="font-semibold text-accent">
            {t("plan.recommended")}:
          </span>{" "}
          {topWhy}
        </p>
      ) : null}
      {errored.size > 0 ? (
        <p
          className="mt-1 px-1 text-[13px]/[1.333] text-[var(--g-error)]"
          role="alert"
          data-testid="chat-plan-error"
        >
          {t("plan.fetchError")}
        </p>
      ) : null}
      <span className="sr-only" role="status" aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}

export default RecommendationPlan;
